import { forwardRef, useEffect, useState, type InputHTMLAttributes } from "react";
import { Check } from "lucide-react";
import { Input } from "@/shared/components/Input";
import { dataHojeInput } from "@/shared/lib/datas";
import {
  aplicarMascaraData,
  interpretarDataDigitada,
  isoParaTexto,
} from "@/shared/lib/dataDigitada";

interface CampoDataNascimentoProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type" | "min" | "max" | "inputMode" | "maxLength" | "placeholder"
> {
  label: string;
  /** Data em AAAA-MM-DD. Fica "" enquanto o texto estiver vazio, incompleto ou inválido. */
  value: string;
  onChange: (iso: string) => void;
  /** Erro vindo do formulário (ex.: campo obrigatório). A mensagem do próprio campo tem prioridade. */
  erro?: string;
  /** Maior data aceita, em AAAA-MM-DD. Padrão: hoje. */
  max?: string;
}

/**
 * Data de nascimento digitada (DD/MM/AAAA) com teclado numérico.
 *
 * Substitui o `<input type="date">` nas telas de nascimento: no seletor nativo
 * de aparelhos antigos, chegar em um ano distante (ex.: 1950) exige muitas
 * voltas. O valor entregue ao formulário continua sendo AAAA-MM-DD.
 */
export const CampoDataNascimento = forwardRef<HTMLInputElement, CampoDataNascimentoProps>(
  ({ value, onChange, onBlur, erro, max, ...resto }, ref) => {
    const limite = max ?? dataHojeInput();
    const [texto, setTexto] = useState(() => isoParaTexto(value));
    const [tocado, setTocado] = useState(false);

    // Valor que muda por fora (abrir um cadastro para editar, limpar o formulário):
    // mostra no campo. Se o texto já representa esse valor, não mexe no que a pessoa digitou.
    useEffect(() => {
      setTexto((atual) => {
        const r = interpretarDataDigitada(atual, { max: limite });
        return (r.ok ? r.iso : "") === value ? atual : isoParaTexto(value);
      });
    }, [value, limite]);

    const resultado = interpretarDataDigitada(texto, { max: limite });
    const mostrarMensagemDoCampo =
      texto !== "" && !resultado.ok && (tocado || !!erro || texto.length === 10);
    const mensagem = mostrarMensagemDoCampo && !resultado.ok ? resultado.mensagem : erro;

    return (
      <div className="flex flex-col gap-1.5">
        <Input
          {...resto}
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD/MM/AAAA"
          maxLength={10}
          value={texto}
          onChange={(ev) => {
            const novo = aplicarMascaraData(ev.target.value);
            setTexto(novo);
            const r = interpretarDataDigitada(novo, { max: limite });
            onChange(r.ok ? r.iso : "");
          }}
          onBlur={(ev) => {
            setTocado(true);
            onBlur?.(ev);
          }}
          erro={mensagem}
        />
        {/* Data por extenso: ajuda a perceber dia e mês trocados antes de salvar. */}
        <div aria-live="polite">
          {resultado.ok && (
            <p className="flex items-center gap-1.5 text-xs text-primary">
              <Check size={14} aria-hidden="true" />
              {resultado.extenso}
            </p>
          )}
        </div>
      </div>
    );
  },
);

CampoDataNascimento.displayName = "CampoDataNascimento";
